import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import type { User, SystemPromptTemplateDetail } from '../types/api';
import DeviceList from '../components/admin/DeviceList';
import UserProfileForm from '../components/admin/UserProfileForm';
import type { Device } from '../types/admin';
import '../styles/Admin.css';

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [templates, setTemplates] = useState<SystemPromptTemplateDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'devices' | 'prompt'>('profile');
  
  // Template assignment state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateMessage, setTemplateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (userId) {
      loadUserData();
    }
  }, [userId]);

  useEffect(() => {
    // Set selected template when user data loads
    if (user) {
      setSelectedTemplateId(user.systemPromptTemplateId || null);
    }
  }, [user]);

  const loadUserData = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      const [userData, devicesData, templatesData] = await Promise.all([
        adminApi.getUser(userId),
        adminApi.getUserDevices(userId),
        adminApi.getAllPromptTemplates()
      ]);
      setUser(userData);
      setDevices(devicesData);
      setTemplates(templatesData.filter(t => t.isActive));
    } catch (err) {
      setError('Failed to load user data');
      console.error('Error loading user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    await loadUserData();
  };

  const handleDevicesUpdate = async () => {
    if (!userId) return;
    const devicesData = await adminApi.getUserDevices(userId);
    setDevices(devicesData);
  };

  const handleTemplateChange = async () => {
    if (!userId) return;
    
    setIsSavingTemplate(true);
    setTemplateMessage(null);
    
    try {
      await adminApi.assignUserPromptTemplate(userId, {
        templateId: selectedTemplateId || null
      });
      
      // Reload user to get updated template info
      const updatedUser = await adminApi.getUser(userId);
      setUser(updatedUser);
      
      setTemplateMessage({
        type: 'success',
        text: selectedTemplateId 
          ? 'Template assigned successfully!' 
          : 'Template cleared - user will use default'
      });
      setTimeout(() => setTemplateMessage(null), 3000);
    } catch (err: any) {
      setTemplateMessage({
        type: 'error',
        text: err.message || 'Failed to update template assignment'
      });
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const hasTemplateChanged = () => {
    const currentTemplateId = user?.systemPromptTemplateId || null;
    return selectedTemplateId !== currentTemplateId;
  };

  const getCategoryColor = (category?: string) => {
    if (!category) return 'bg-gray-100 text-gray-700';
    const colors: Record<string, string> = {
      'General': 'bg-blue-100 text-blue-700',
      'Accessibility': 'bg-green-100 text-green-700',
      'Technical': 'bg-purple-100 text-purple-700',
      'Senior-Friendly': 'bg-amber-100 text-amber-700',
      'Kid-Safe': 'bg-pink-100 text-pink-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const getSelectedTemplate = () => {
    if (!selectedTemplateId) return null;
    return templates.find(t => t.id === selectedTemplateId);
  };

  const getCurrentTemplate = () => {
    if (!user?.systemPromptTemplateId) return null;
    return templates.find(t => t.id === user.systemPromptTemplateId);
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">Loading user data...</div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="admin-container">
        <div className="error-message">{error || 'User not found'}</div>
        <button onClick={() => navigate('/admin')} className="btn-back">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button onClick={() => navigate('/admin')} className="btn-back">
          ← Back to Users
        </button>
        <h1>{user.displayName}</h1>
        <p className="subtitle">{user.email || 'No email provided'}</p>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile Settings
        </button>
        <button 
          className={`tab ${activeTab === 'devices' ? 'active' : ''}`}
          onClick={() => setActiveTab('devices')}
        >
          Devices ({devices.length})
        </button>
        <button 
          className={`tab ${activeTab === 'prompt' ? 'active' : ''}`}
          onClick={() => setActiveTab('prompt')}
        >
          Prompt Customization
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'profile' && userId && (
          <UserProfileForm 
            user={user} 
            userId={userId}
            onUpdate={handleProfileUpdate}
          />
        )}
        
        {activeTab === 'devices' && userId && (
          <DeviceList 
            devices={devices}
            userId={userId}
            onUpdate={handleDevicesUpdate}
          />
        )}
        
        {activeTab === 'prompt' && userId && (
          <div className="admin-card">
            <h3>User-Specific Prompt Customization</h3>
            <p className="subtitle">Customize how the AI assistant interacts with this user</p>
            
            {/* Template Assignment Section */}
            <div className="form-section mt-4">
              <h4 className="font-semibold text-gray-900 mb-3">System Prompt Template</h4>
              
              {templateMessage && (
                <div className={`mb-4 p-3 rounded-lg ${
                  templateMessage.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-700' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {templateMessage.text}
                </div>
              )}

              {/* Current Assignment Display */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Current template:</p>
                {getCurrentTemplate() ? (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{getCurrentTemplate()?.name}</span>
                    {getCurrentTemplate()?.category && (
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getCategoryColor(getCurrentTemplate()?.category)}`}>
                        {getCurrentTemplate()?.category}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500 italic">Using default template</span>
                )}
              </div>
              
              {/* Template Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Template
                </label>
                <select
                  value={selectedTemplateId || ''}
                  onChange={(e) => setSelectedTemplateId(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wise focus:border-transparent"
                >
                  <option value="">Use Default Template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} {template.category ? `(${template.category})` : ''} {template.isDefault ? '⭐' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected Template Preview */}
              {getSelectedTemplate() && (
                <div className="mb-4 p-3 border border-gray-200 rounded-lg bg-white">
                  <p className="text-sm font-medium text-gray-700 mb-1">Selected template preview:</p>
                  <p className="text-sm text-gray-600">{getSelectedTemplate()?.description || 'No description'}</p>
                </div>
              )}

              <button
                onClick={handleTemplateChange}
                disabled={!hasTemplateChanged() || isSavingTemplate}
                className="px-4 py-2 bg-wise text-white rounded-lg hover:bg-wise-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingTemplate ? 'Saving...' : 'Save Template Assignment'}
              </button>
            </div>

            {/* Context Variables Info */}
            <div className="form-section mt-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                <h4 className="font-semibold text-blue-900 mb-2">User Context Variables:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>Preferences:</strong> Injected via {'{{'}PREFERENCES{'}}'}</li>
                  <li>• <strong>Notes:</strong> Injected via {'{{'}USER_NOTES{'}}'}</li>
                  <li>• <strong>Rules:</strong> Injected via {'{{'}USER_RULES{'}}'}</li>
                  <li>• <strong>Devices:</strong> Automatically formatted via {'{{'}RELEVANT_DEVICES{'}}'}</li>
                </ul>
                <p className="text-sm text-blue-800 mt-3">
                  Configure these values in the <strong>Profile Settings</strong> tab.
                  To manage templates, go to <strong>Admin → Prompts</strong>.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
